import { IconButton } from "@dropbox/dig-components/buttons";
import { Title } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { AddCircleLine, CloseLine } from "@dropbox/dig-icons/assets";
import { PAPEvent } from "@mirage/analytics/events/base/event";
import { PAP_Click_NewSessionButton } from "@mirage/analytics/events/types/click_new_session_button";
import { AIToolLibraryConfig } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibrary";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { UserMessageOptions } from "@mirage/service-conversation/types";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo, SetStateAction } from "react";
import { ScenarioBackButton } from "../components/ScenarioBackButton";
import styles from "./ConversationAssistantPageContent.module.css";
import { ConversationList } from "./ConversationList";

export interface ConversationAssistantPageContentSidePanelButtonsProps {
  variant?: "default" | "condensed" | "workflow-agent-preview";
  showEmptyChatInstructions: boolean;
  onCloseWorkflowAgentPreview?: () => void;
  openLibrary: (config?: AIToolLibraryConfig) => void;
  handleStartNewSessionWithState: (
    routerState?: ConversationRouterState,
  ) => void;
  handleStartNewSession: () => void;
  logComposeEvent: (event: PAPEvent) => void;
  setEmptyChatOption: (
    option: ClickableEmptyChatInstructionsOption | undefined,
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scenarioMeta?: any;
  setUserMessageOptions: (options: SetStateAction<UserMessageOptions>) => void;
}

export const ConversationAssistantPageContentSidePanelButtons = memo(
  ({
    variant = "default",
    showEmptyChatInstructions,
    onCloseWorkflowAgentPreview,
    openLibrary,
    handleStartNewSessionWithState,
    handleStartNewSession,
    logComposeEvent,
    setEmptyChatOption,
    scenarioMeta,
    setUserMessageOptions,
  }: ConversationAssistantPageContentSidePanelButtonsProps) => {
    const isEmbed = isEmbedPage();

    const addNewSessionButton = (
      <DigTooltip title={i18n.t("compose_new_session")}>
        <IconButton
          variant="borderless"
          onClick={() => {
            handleStartNewSession();
            logComposeEvent(
              PAP_Click_NewSessionButton({
                dashActionSurface: "conversation_page",
                actionSurfaceComponent: "top_nav",
                featureLine: "assist",
              }),
            );
          }}
          className={classNames({
            [styles.NewSessionButtonEmbed]: isEmbed,
          })}
        >
          <UIIcon src={AddCircleLine} />
        </IconButton>
      </DigTooltip>
    );

    const sidePanelButtons =
      variant === "workflow-agent-preview" ? (
        <div className={styles.WorkflowAgentPreviewSidePanelButtons}>
          <IconButton
            variant="opacity"
            shape="circular"
            onClick={onCloseWorkflowAgentPreview}
          >
            <UIIcon src={CloseLine} />
          </IconButton>
          <Title size="small" weightVariant="emphasized">
            {i18n.t("assistant_ai_tool_preview_button")}
          </Title>
        </div>
      ) : (
        <>
          {isEmbed ? (
            <>
              {addNewSessionButton}
              <ConversationList
                showListPreview={showEmptyChatInstructions}
                onOpenTemplates={() => openLibrary({ tab: "templates" })}
                handleStartNewSessionWithState={handleStartNewSessionWithState}
                logComposeEvent={logComposeEvent}
                clearEmptyChatOption={() => setEmptyChatOption(undefined)}
                setUserMessageOptions={setUserMessageOptions}
              />
            </>
          ) : (
            <>
              <ConversationList
                showListPreview={showEmptyChatInstructions}
                onOpenTemplates={() => openLibrary({ tab: "templates" })}
                handleStartNewSessionWithState={handleStartNewSessionWithState}
                logComposeEvent={logComposeEvent}
                clearEmptyChatOption={() => setEmptyChatOption(undefined)}
                setUserMessageOptions={setUserMessageOptions}
              />
              {addNewSessionButton}
            </>
          )}

          {!isEmbed &&
            scenarioMeta &&
            scenarioMeta?.type !== "workflow_agent" && (
              <ScenarioBackButton scenario={scenarioMeta} />
            )}
        </>
      );

    return sidePanelButtons;
  },
);

ConversationAssistantPageContentSidePanelButtons.displayName =
  "ConversationAssistantPageContentSidePanelButtons";
